<script lang="ts">
  // Lightweight dual-series area+line chart (inline SVG, no dependency).
  // Renders two overlaid filled areas with crisp top lines, scaled to a shared
  // max. Built for the weekly posts/comments trend but generic over numbers.
  let {
    a,
    b = [],
    c = [],
    colorA = 'var(--color-primary)',
    colorB = 'var(--color-secondary)',
    colorC = 'var(--color-violet)',
    height = 120,
  }: {
    a: number[];
    b?: number[];
    c?: number[]; // optional third series, drawn as a line on its OWN scale (secondary axis)
    colorA?: string;
    colorB?: string;
    colorC?: string;
    height?: number;
  } = $props();

  const W = 100; // viewBox units; SVG scales to container width
  const max = $derived(Math.max(1, ...a, ...b));
  const maxC = $derived(Math.max(1, ...c)); // c is a different quantity (member count) → own scale
  const n = $derived(Math.max(a.length, b.length, c.length));

  function x(i: number): number {
    return n <= 1 ? 0 : (i / (n - 1)) * W;
  }
  function y(v: number): number {
    return height - (v / max) * height;
  }
  function lineFrom(s: number[], yf: (v: number) => number): string {
    if (s.length === 0) return '';
    return s.map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(2)},${yf(v).toFixed(2)}`).join(' ');
  }
  const line = (s: number[]): string => lineFrom(s, y);
  function area(s: number[]): string {
    if (s.length === 0) return '';
    return `${line(s)} L${x(s.length - 1).toFixed(2)},${height} L0,${height} Z`;
  }
</script>

<svg
  viewBox="0 0 {W} {height}"
  preserveAspectRatio="none"
  style="display:block; width:100%; height:{height}px"
  role="img"
  aria-label="trend chart"
>
  {#if b.length}
    <path d={area(b)} fill={colorB} opacity="0.18" />
    <path d={line(b)} fill="none" stroke={colorB} stroke-width="0.8" vector-effect="non-scaling-stroke" opacity="0.7" />
  {/if}
  <path d={area(a)} fill={colorA} opacity="0.22" />
  <path d={line(a)} fill="none" stroke={colorA} stroke-width="1" vector-effect="non-scaling-stroke" />
  {#if c.length}
    <path d={lineFrom(c, (v) => height - (v / maxC) * height)} fill="none" stroke={colorC} stroke-width="1.2" stroke-dasharray="3 2" vector-effect="non-scaling-stroke" opacity="0.9" />
  {/if}
</svg>
